import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const BrandForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState("");

    useEffect(() => {
        if (!isEditMode) { setLoading(false); return; }
        const fetchBrand = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/brands');
                const found = data.brands.find((b) => b._id === id);
                if (found) {
                    setName(found.name);
                } else {
                    enqueueSnackbar("Brand not found", { variant: "error" });
                    navigate('/admin/brands');
                }
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load brand", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchBrand();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const submitHandler = async (e) => {
        e.preventDefault();
        if (!name.trim()) return enqueueSnackbar("Please enter a brand name", { variant: "warning" });

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/brand/${id}`, { name });
                enqueueSnackbar("Brand updated — existing products using the old name were updated too", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/brand/new', { name });
                enqueueSnackbar("Brand created", { variant: "success" });
            }
            navigate('/admin/brands');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save brand", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Brand | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6 max-w-lg" id="brandform">
                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Brand</h1>

                <TextField
                    label="Brand Name"
                    variant="outlined"
                    size="small"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    helperText={isEditMode ? "Renaming updates every product currently using this brand" : undefined}
                />

                <div className="flex justify-end">
                    <input form="brandform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Brand"} />
                </div>
            </form>
        </>
    );
};

export default BrandForm;
